import React from 'react';
import { useFlags, useLabels, useRequestData } from 'wsm-common-data';
import { HighlightedAttribute } from 'wsm-highlighted-attribute';
import { VehicleViews } from 'wsm-vehicle-views';
import propDefs from '../proptypes';
import { setClassNames, getVehicleTypeLabel } from '../utils/utility';

const VehicleTitle = ({
	vehicle,
	prefs,
	displayAttrsList,
	vehicleViews = null
}) => {
	const flags = useFlags();
	const labels = useLabels();
	const { deviceType } = useRequestData();
	const displayCustomTitle = vehicle.customTitle.length;
	const customTitleLine = displayCustomTitle ? (
		<span
			className={setClassNames(
				'additional-details',
				'd-block',
				'mt-0',
				'mb-1',
				'font-weight-normal',
				prefs.titleDetailsSize,
				prefs.titleDetailsColor
			)}
		>
			{vehicle.customTitle}
		</span>
	) : null;

	const displayAdditionalDetails =
		displayAttrsList.includes('vin') &&
		displayAttrsList.includes('stockNumber');

	const isSupportedFuelTypeAttr =
		(vehicle.normalFuelType === 'Electric' ||
			vehicle.normalFuelType === 'Hybrid') &&
		prefs.enableFuelTypeHighlight === 'true';

	const isVehicleViewsEnabled =
		flags['ws-vehicle-views-component-rollout'] &&
		prefs.enableVehicleViews === 'true' &&
		vehicleViews >= prefs.vehicleViews_minimumViews;

	const additionalDetails = displayAdditionalDetails ? (
		// ATTN: We need spaces between <li>'s
		// This fixes a strange double click selection issue in browsers
		<ul
			className={setClassNames(
				'additional-details',
				'list-inline',
				'mb-0',
				'font-weight-normal',
				prefs.titleDetailsSize,
				prefs.titleDetailsColor,
				prefs.enableFuelTypeHighlight ? 'mt-3' : ''
			)}
		>
			{isSupportedFuelTypeAttr && (
				<li>
					<HighlightedAttribute
						title={vehicle.normalFuelType}
						electricIcon={prefs.highlightedFuelTypeElectricIcon}
						hybridIcon={prefs.highlightedFuelTypeHybridIcon}
						type="fuelType"
					/>
				</li>
			)}
			{isVehicleViewsEnabled && (
				<li>
					<VehicleViews
						vehicleViews={vehicleViews}
						disclaimerLabel={prefs.vehicleViews_disclaimerLabel}
						displayStyle={prefs.vehicleViews_displayStyle}
						icon={prefs.vehicleViews_icon}
						iconPopular={prefs.vehicleViews_iconPopular}
						minimumViews={prefs.vehicleViews_minimumViews}
						timeFrame={prefs.vehicleViews_timeFrame}
					/>
				</li>
			)}
			{vehicle.vin && prefs.enableVin === 'true' && (
				<li className="align-text-bottom">
					{`${labels.get('VIN')}: `}
					{vehicle.vin}{' '}
				</li>
			)}
			{vehicle.stockNumber && prefs.enableStockNumber === 'true' && (
				<li className="align-text-bottom">
					{` ${labels.get('STOCK')}: `}
					{vehicle.stockNumber}{' '}
				</li>
			)}
			{/* This may be added back in at some point */}
			{/* {modelCode && <li> {labels.get('MODEL')}: {modelCode} </li>} */}
		</ul>
	) : null;

	let vehicleTitle;

	if (flags['ws-vehicle-title-enable-idc-integration']) {
		vehicleTitle = vehicle.title;
	} else {
		const vehicleTypeLabel = getVehicleTypeLabel(
			labels,
			vehicle.type.toUpperCase(), // Temp until VCDA data for type is fixed (capitalized)
			vehicle.certified,
			prefs.usedVehicleTerm
		);

		vehicleTitle = [
			`${vehicleTypeLabel || ''} ${vehicle.year || ''} ${vehicle.make ||
				''}`,
			`${vehicle.model || ''} ${vehicle.trim || ''} ${vehicle.bodyStyle ||
				''}`
		];
	}

	let vehicleMarkup = <h1>{labels.get('VEHICLE_UNAVAILABLE')}</h1>;

	if (vehicle.make && vehicle.model && vehicle.year) {
		vehicleMarkup = (
			<div>
				<h1
					className={setClassNames(
						'vehicle-title',
						'm-0',
						'line-height-reset',
						deviceType !== 'DESKTOP' && 'h2'
					)}
				>
					<span
						className={setClassNames(
							'd-block',
							prefs.titleHeading,
							prefs.titleHeadingColor,
							prefs.titleHeadingWeight,
							prefs.titleHeadingSize
						)}
					>
						{vehicleTitle.length > 1 && vehicleTitle[0]}
					</span>
					<span
						className={setClassNames(
							prefs.titleHeadingTagLineWeight,
							prefs.titleHeadingTagLineSize
						)}
					>
						{vehicleTitle.length > 1
							? vehicleTitle[1]
							: vehicleTitle[0]}
					</span>
					{customTitleLine}
				</h1>
				{additionalDetails}
			</div>
		);
	}

	if (Object.keys(vehicle).length === 0 && vehicle.constructor === Object) {
		return null;
	}

	return vehicleMarkup;
};

VehicleTitle.propTypes = {
	vehicle: propDefs.settings.vehicle,
	prefs: propDefs.settings.prefs.isRequired,
	displayAttrsList: propDefs.settings.displayAttrsList,
	vehicleViews: propDefs.settings.vehicleViews
};

VehicleTitle.defaultProps = {
	vehicle: propDefs.defaults.vehicle,
	displayAttrsList: propDefs.defaults.displayAttrsList,
	vehicleViews: propDefs.defaults.vehicleViews
};

export default VehicleTitle;
